import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import _ from 'lodash';
import cn from 'classnames';
import cogoToast from 'cogo-toast';
import moment from 'moment';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowCircleUp, faArrowLeft } from '@fortawesome/pro-solid-svg-icons';

import { affiliateRates, commissionDisclaimerDisplay, disclaimerNotes } from '@shopmy/universal';

import { getMerchantV2Detail, updateMerchant, addMerchantTestCase, updateMerchantTestCase } from '../../api/merchants';
import { SNAPSHOT_SOURCES, getSourceLabel, getSourceTagClass, formatMerchantRate } from './merchantSourceHelpers';
import PartnerBadge from './PartnerBadge';
import './MerchantDetail.scss';

const renderNoteWithLinks = note =>
  disclaimerNotes.splitNoteIntoSegments(note).map((segment, index) =>
    segment.url ? (
      <a key={index} href={segment.url} target='_blank' rel='noopener noreferrer'>
        {segment.text}
      </a>
    ) : (
      <React.Fragment key={index}>{segment.text}</React.Fragment>
    )
  );

const FACT_KIND_LABELS = { base: 'Base', conditional: 'Conditional', tier: 'Tier', bonus: 'Bonus' };
const GENERIC_SUBJECTS = {
  sku: 'Certain SKUs',
  product: 'Select products',
  category: 'Select categories',
  brand: 'Select brands',
  customer: 'Select customers',
  voucher: 'Voucher code orders',
  sale: 'Marked-down items',
  region: 'Certain regions',
  amount: 'Certain order amounts',
  payment: 'Certain payment types',
  traffic: 'Certain referral sources'
};

const MerchantDetail = ({ id }) => {
  const [detail, setDetail] = useState(null);
  const [error, setError] = useState(null);
  const [showRaw, setShowRaw] = useState(false);

  useEffect(() => {
    let isActive = true;
    setDetail(null);
    setError(null);
    setShowRaw(false);
    getMerchantV2Detail(id)
      .then(result => isActive && setDetail(result))
      .catch(requestError => isActive && setError(requestError || 'Could not load this merchant.'));
    return () => {
      isActive = false;
    };
  }, [id]);

  if (error) {
    return (
      <div className='merchant-detail'>
        <BackLink />
        <div className='merchant-detail-error'>{String(error)}</div>
      </div>
    );
  }
  if (!detail) {
    return (
      <div className='merchant-detail'>
        <BackLink />
        <div className='merchant-detail-loading'>Loading merchant...</div>
      </div>
    );
  }

  const {
    merchant,
    brand,
    domainMerchants,
    programMerchants,
    commissionDisclaimer,
    commissionDisclaimerVersions,
    shopmyRates,
    creatorShare
  } = detail;
  const display = commissionDisclaimer?.disclaimerDisplay;
  const sourceLabel = getSourceLabel(merchant.source);
  const rate = formatMerchantRate(merchant);

  const merchantTerms = [
    { label: 'Rate', detail: rate ? `${rate}${merchant.payoutType ? ` · ${merchant.payoutType}` : ''}` : 'No rate on file', warn: !rate },
    merchant.network_name && merchant.network_name !== merchant.name && { label: 'Network name', detail: merchant.network_name },
    { label: 'Brand account', detail: brand ? `${brand.name} (Brand ID ${brand.id})` : 'Not tied to a brand account' },
    merchant.isClosedProgram && { label: 'Closed program', detail: 'Invite-only — only creators with a custom rate earn commission.', warn: true },
    { label: 'Manual disclaimer', detail: merchant.disclaimer || '—' },
    { label: 'App deep linking', detail: merchant.appDeepLinkingEnabled ? 'Enabled — pins can open the merchant app directly' : 'Off' },
    { label: 'Monthly pins', detail: merchant.monthlyPins.toLocaleString() },
    { label: 'First seen', detail: formatObservedAt(merchant.createdAt) },
    { label: 'Last updated', detail: formatObservedAt(merchant.updatedAt) }
  ].filter(Boolean);

  return (
    <div className='merchant-detail'>
      <BackLink />

      <div className='merchant-detail-header'>
        <h1>
          {merchant.name}
          {merchant.Brand_id && <PartnerBadge size={22} title={`Brand partner (Brand ID ${merchant.Brand_id})`} />}
        </h1>
        <div className='merchant-detail-subheader'>
          <span className={cn('network-tag', getSourceTagClass(merchant.source))}>{sourceLabel}</span>
          {merchant.isSMSWinner ? (
            <span className='live-status live'>✓ Live — creators earn through this merchant</span>
          ) : (
            <span className='live-status'>Not live — creators earn through another network on this domain</span>
          )}
          {merchant.isClosedProgram && <span className='program-flag closed'>Closed program</span>}
          <a href={`https://${merchant.domain}`} target='_blank' rel='noopener noreferrer'>
            {merchant.domain}
          </a>
          {merchant.stable_merchant_id && <span className='network-id'>Network ID {merchant.stable_merchant_id}</span>}
          <span className='network-id'>Merchant #{merchant.id}</span>
        </div>
      </div>

      <div className='merchant-detail-columns'>
        <div className='merchant-detail-main'>
          <section>
            <h2>Merchant</h2>
            <div className='term-list'>
              {merchantTerms.map(term => (
                <TermRow key={term.label} {...term} />
              ))}
            </div>
          </section>

          {shopmyRates && <ShopmyRatesSection rates={shopmyRates} merchant={merchant} />}

          <section>
            <h2>
              Networks on {merchant.domain} <span className='count'>{domainMerchants.length}</span>
            </h2>
            <div className='term-list'>
              {domainMerchants.map(row => (
                <SiblingRow key={row.id} row={row} currentId={merchant.id} primary={getSourceLabel(row.source)} secondary={row.name} />
              ))}
            </div>
          </section>

          {programMerchants.length > 0 && (
            <section>
              <h2>
                Other domains on this {sourceLabel} program <span className='count'>{programMerchants.length}</span>
              </h2>
              <div className='empty-note'>Same network program ID — these rows share the commission disclaimer below.</div>
              <div className='term-list'>
                {programMerchants.map(row => (
                  <SiblingRow key={row.id} row={row} currentId={merchant.id} primary={row.domain} secondary={row.name} />
                ))}
              </div>
            </section>
          )}

          <section>
            <h2>
              Commission disclaimer
              {commissionDisclaimer && <span className='count'>v{commissionDisclaimer.versionNumber}</span>}
              {commissionDisclaimer && (
                <Link className='section-link' to={`/merchant-commission-disclaimers/${commissionDisclaimer.id}`}>
                  Open in Commission Disclaimers ↗
                </Link>
              )}
            </h2>
            {!commissionDisclaimer ? (
              <div className='empty-note'>{describeMissingDisclaimer(merchant)}</div>
            ) : (
              <>
                <div className='disclaimer-status'>
                  <span className={cn('status-pill', commissionDisclaimer.isAvailable ? 'available' : 'unavailable')}>
                    {commissionDisclaimer.isAvailable ? 'Available' : 'Unavailable'}
                  </span>
                  {display?.mode && <span className='status-pill'>{display.mode}</span>}
                  {display?.fallbackReason && <span className='status-pill warn'>{display.fallbackReason.replace(/_/g, ' ')}</span>}
                </div>
                {display ? (
                  <>
                    <div className='disclaimer-text'>{display.disclaimer}</div>
                    <div className='disclaimer-lines'>
                      {(display.lines || []).map(line => (
                        <div key={`${line.type}-${line.text}`} className='disclaimer-line'>
                          <span className={cn('line-type', line.type)}>{line.type}</span>
                          <span className='line-text'>{line.text}</span>
                          <span className='line-rules'>{(line.ruleKeys || []).length} rule{(line.ruleKeys || []).length === 1 ? '' : 's'}</span>
                        </div>
                      ))}
                    </div>
                  </>
                ) : (
                  <div className='empty-note'>This version records the program as unavailable — no display was composed.</div>
                )}
                <div className='term-list'>
                  <TermRow label='First observed' detail={formatObservedAt(commissionDisclaimer.firstObservedAt)} />
                  <TermRow label='Last observed' detail={formatObservedAt(commissionDisclaimer.lastObservedAt)} />
                  {display?.omittedFactCount > 0 && <TermRow label='Omitted facts' detail={String(display.omittedFactCount)} />}
                  {display?.warnings?.length > 0 && <TermRow label='Warnings' detail={display.warnings.join(' ')} warn />}
                  <TermRow
                    label='Previous version'
                    detail={
                      commissionDisclaimer.previousMerchantCommissionDisclaimer_id ? (
                        <Link to={`/merchant-commission-disclaimers/${commissionDisclaimer.previousMerchantCommissionDisclaimer_id}`}>
                          #{commissionDisclaimer.previousMerchantCommissionDisclaimer_id}
                        </Link>
                      ) : (
                        '—'
                      )
                    }
                  />
                  <TermRow label='Rate hash' detail={<code title={commissionDisclaimer.semanticHash}>{commissionDisclaimer.semanticHash}</code>} />
                  <TermRow label='Snapshot key' detail={<code>{commissionDisclaimer.snapshotKey}</code>} />
                </div>
              </>
            )}
          </section>

          {commissionDisclaimer && (
            <section>
              <h2>
                Rate facts <span className='count'>{commissionDisclaimer.rateFacts.length}</span>
              </h2>
              {!commissionDisclaimer.rateFacts.length && <div className='empty-note'>No rate facts were decoded for this version.</div>}
              <div className='term-list'>
                {sortRateFacts(commissionDisclaimer.rateFacts).map(fact => (
                  <div key={fact.ruleKey} className='rate-fact-row'>
                    <div className='rate-fact-value'>
                      <span className={cn('rate-pill', fact.kind)}>{formatFactRate(fact)}</span>
                    </div>
                    <div className='rate-fact-text'>
                      <div className='rate-fact-label'>
                        {describeFactSubject(fact)}
                        <span className='rate-fact-kind'>{FACT_KIND_LABELS[fact.kind] || fact.kind}</span>
                        {describeFactWindow(fact) && <span className='rate-fact-window'>{describeFactWindow(fact)}</span>}
                      </div>
                      {describeFactRule(fact) && <div className='rate-fact-description'>{describeFactRule(fact)}</div>}
                      <div className='rate-fact-key'>{fact.ruleKey}</div>
                    </div>
                  </div>
                ))}
              </div>
            </section>
          )}

          {commissionDisclaimerVersions.length > 1 && (
            <section>
              <h2>
                Version history <span className='count'>{commissionDisclaimerVersions.length}</span>
              </h2>
              <div className='term-list'>
                {commissionDisclaimerVersions.map(version => (
                  <Link key={version.id} className={cn('version-row', { current: version.isCurrent })} to={`/merchant-commission-disclaimers/${version.id}`}>
                    <span className='version-number'>v{version.versionNumber}</span>
                    <span className={cn('status-pill', version.isCurrent ? 'available' : version.isAvailable ? '' : 'unavailable')}>
                      {version.isCurrent ? 'current' : version.isAvailable ? 'superseded' : 'unavailable'}
                    </span>
                    <span className='version-text'>{version.disclaimerDisplay?.disclaimer || '—'}</span>
                    <span className='version-dates'>
                      {formatObservedAt(version.firstObservedAt, 'MMM D, YYYY')} → {formatObservedAt(version.lastObservedAt, 'MMM D, YYYY')}
                    </span>
                  </Link>
                ))}
              </div>
            </section>
          )}

          <section>
            <button className='raw-toggle' onClick={() => setShowRaw(!showRaw)}>
              {showRaw ? 'Hide' : 'Show'} raw {sourceLabel} merchant record
            </button>
            {showRaw && <pre className='raw-payload'>{merchant.raw ? JSON.stringify(merchant.raw, null, 2) : 'No raw record stored on this row.'}</pre>}
          </section>
        </div>

        <div className='merchant-detail-side'>
          <div className='creator-preview-note'>
            {detail.shopmyRatesUnavailable
              ? 'Creator preview — this brand is on our network so there is no commission split.'
              : creatorShare === 100
              ? 'Creator preview — this brand pays a network fee, so creators see these exact rates.'
              : `Creator preview — based on the ${creatorShare}% creator revenue share.`}
          </div>
          <CreatorRatePreview detail={detail} onDetailChange={setDetail} />
        </div>
      </div>
    </div>
  );
};

const ShopmyRatesSection = ({ rates, merchant }) => {
  const overrides = [
    ...rates.collectionRates.map(rate => ({
      key: `collection-${rate.id}`,
      kind: 'collection',
      rate: rate.rate,
      title: rate.title,
      note: describeCatalogGroupType(rate.type),
      reference: `CatalogGroup #${rate.CatalogGroup_id} · CustomAffiliateRate #${rate.id}`
    })),
    ...rates.productRates.map(rate => ({
      key: `product-${rate.id}`,
      kind: 'product',
      rate: rate.rate,
      title: rate.title,
      note: rate.sizeTag,
      reference: `${rate.SpecificVariant_id ? `Variant #${rate.SpecificVariant_id}` : `VariantSibling #${rate.SpecificVariantSibling_id}`} · CustomAffiliateRate #${rate.id}`
    }))
  ];
  const terms = [
    {
      label: 'Standard rate',
      detail: merchant.isClosedProgram
        ? `${formatPercent(rates.standardRate)} — closed program, so only creators the brand approves earn it`
        : formatPercent(rates.standardRate, 'Not set — the brand has not finished onboarding'),
      warn: rates.standardRate === null
    },
    rates.returningCustomerRate !== null &&
      rates.returningCustomerRate !== rates.standardRate && { label: 'Returning customers', detail: formatPercent(rates.returningCustomerRate) },
    {
      label: 'Creator share',
      detail: rates.brandPaysNetworkFee
        ? `100% — ${merchant.name} pays ShopMy a network fee (${rates.networkFee.value}% of ${rates.networkFee.basis === 'gmv' ? 'GMV' : 'commission'}), so creators see these exact rates`
        : `${rates.creatorShare}% of the listed rates${rates.hasCreatorShareOverride ? ' (brand-specific override)' : ''}`
    }
  ].filter(Boolean);

  return (
    <section>
      <h2>
        ShopMy rates <span className='count'>{overrides.length}</span>
      </h2>
      <div className='empty-note'>Set by the brand in their ShopMy account — the same numbers the live creator rate math reads.</div>
      <div className='term-list'>
        {terms.map(term => (
          <TermRow key={term.label} {...term} />
        ))}
      </div>
      {overrides.length > 0 && (
        <div className='term-list shopmy-rate-overrides'>
          {overrides.map(override => (
            <div key={override.key} className='rate-fact-row'>
              <div className='rate-fact-value'>
                <span className={cn('rate-pill', override.kind)}>{formatPercent(override.rate)}</span>
              </div>
              <div className='rate-fact-text'>
                <div className='rate-fact-label'>
                  {override.title}
                  <span className='rate-fact-kind'>{override.kind === 'collection' ? 'Collection rate' : 'Product rate'}</span>
                </div>
                {override.note && <div className='rate-fact-description'>{override.note}</div>}
                <div className='rate-fact-key'>{override.reference}</div>
              </div>
            </div>
          ))}
        </div>
      )}
    </section>
  );
};

const SiblingRow = ({ row, currentId, primary, secondary }) => {
  const isCurrent = row.id === currentId;
  const title = row.disclaimer ? `Manual disclaimer: ${row.disclaimer}` : undefined;
  const content = (
    <>
      <span className={cn('network-tag', getSourceTagClass(row.source))}>{getSourceLabel(row.source)}</span>
      <span className='sibling-primary'>{primary}</span>
      <span className='sibling-secondary'>
        {secondary}
        {row.stable_merchant_id && <span className='sibling-network-id'>ID {row.stable_merchant_id}</span>}
      </span>
      {row.isClosedProgram && <span className='sibling-closed'>Closed</span>}
      <span className='sibling-rate'>{formatMerchantRate(row) || 'No rate'}</span>
      {row.isSMSWinner ? <span className='live-check'>✓ live</span> : <span className='live-dash'>—</span>}
      <span className='sibling-updated'>{row.updatedAt ? moment(row.updatedAt).format('MMM D, YYYY') : '—'}</span>
      {isCurrent && <span className='sibling-current'>viewing</span>}
    </>
  );
  return isCurrent ? (
    <div className='sibling-row current' title={title}>
      {content}
    </div>
  ) : (
    <Link className='sibling-row' to={`/merchantsV2/${row.id}`} title={title}>
      {content}
    </Link>
  );
};

/*
  The Figma creator rate card. Headline rule: the card leads with the highest
  creator rate on the merchant. Non-partners get an "Up to" prefix whenever
  other rates differ, partners get the plain number plus an "Increased rate"
  chip when something beats the regular rate — the bolded row below always
  says where the headline comes from. Rates are the creator's share of the
  listed rate, rounded like the live creator surfaces: whole percents, one
  decimal under 3%.
*/
const toCreatorRate = (value, share) => affiliateRates.roundCreatorRate((value * share) / 100);
const MAX_PREVIEW_LINES = 8;

const CreatorRatePreview = ({ detail, onDetailChange }) => {
  const { merchant, creatorShare: share, shopmyRates } = detail;
  const [noteDraft, setNoteDraft] = useState(null);
  const [savingNote, setSavingNote] = useState(false);
  const note = merchant.disclaimer;

  // The lock is written before the note: the nightly rebuild only keeps a disclaimer
  // with isDisclaimerLocked set, so a save that fails halfway must never leave it unlocked.
  const saveNote = async () => {
    const value = noteDraft.trim() || null;
    // Merchants.disclaimer is VARCHAR(255); a longer note would truncate mid-link for creators.
    if (value && value.length > 255) {
      cogoToast.error(`Notes are limited to 255 characters — this one is ${value.length}.`);
      return;
    }
    setSavingNote(true);
    try {
      let testCase = detail.testCase;
      if (testCase?.id) {
        await updateMerchantTestCase(testCase, { disclaimer: value, isDisclaimerLocked: true });
        testCase = { ...testCase, disclaimer: value, isDisclaimerLocked: true };
      } else if (merchant.domain) {
        testCase = await addMerchantTestCase({
          domain: merchant.domain,
          name: merchant.name,
          source: merchant.source,
          fullPayout: merchant.fullPayout ?? 0,
          ...(value === null ? {} : { disclaimer: value }),
          isDisclaimerLocked: true
        });
      }
      await updateMerchant(merchant, { disclaimer: value });
      onDetailChange({ ...detail, merchant: { ...merchant, disclaimer: value }, testCase });
      setNoteDraft(null);
    } catch (saveError) {
      cogoToast.error(String(saveError?.data?.error || saveError || 'Could not save the note. Please try again.'));
    }
    setSavingNote(false);
  };

  const isPercentRate = merchant.rateType !== 'fixed_fee' && merchant.fullPayout !== null && merchant.fullPayout !== undefined;
  const baseValue = isPercentRate ? toCreatorRate(merchant.fullPayout, share) : null;
  const baseLabel = merchant.Brand_id ? 'Regular rate' : 'Most items';
  const composed = detail.creatorCommissionDisclaimer;

  let rows;
  if (composed?.lines?.length) {
    // One brain: the rows are the server composer's lines at creator rates, verbatim.
    rows = composed.lines.map(line => ({
      label: line.subjects
        ? _.upperFirst(line.subjects)
        : line.type === 'base'
        ? baseLabel
        : line.type === 'boost'
        ? 'Limited time'
        : line.type === 'varies'
        ? 'Rate varies'
        : _.upperFirst(line.text),
      sublabel: commissionDisclaimerDisplay.lineSubjectSamplesText(line),
      valueText: rateTextForLine(line),
      value: line.rate?.value ?? null,
      unit: line.rate?.unit ?? null,
      isExclusion: line.type === 'exclusion',
      until: line.endsAt ? moment(line.endsAt).format('MMM D, YYYY') : null,
      isBase: line.type === 'base'
    }));
  } else {
    // ShopMy-network brands have no composed network disclaimer; their only extra
    // line is the returning-customer rate, run through the same creator share.
    const returningValue = shopmyRates?.returningCustomerRate != null ? toCreatorRate(shopmyRates.returningCustomerRate, share) : null;
    rows =
      returningValue !== null && returningValue !== baseValue
        ? [{ label: 'Returning customers', valueText: `${returningValue}%`, value: returningValue, until: null }]
        : [];
    if (baseValue !== null && rows.length) {
      rows.unshift({ label: baseLabel, valueText: `${baseValue}%`, value: baseValue, until: null, isBase: true });
    }
  }

  rows.forEach((row, index) => {
    if (row.sublabel && rows.slice(0, index).some(prev => prev.sublabel === row.sublabel)) row.sublabel = 'Same as above';
  });

  // A lone base row just repeats the headline — creator surfaces drop it too.
  if (rows.length === 1 && rows[0].isBase) rows = [];

  // Mirrors the creator surfaces: one flat rate with no note (and no ShopMy rate overrides,
  // which generate a note creator-side) means creators get no popover at all.
  const creatorsSeeBreakdown =
    !!note ||
    commissionDisclaimerDisplay.displayHasDisclosure(composed) ||
    !!(shopmyRates?.collectionRates?.length || shopmyRates?.productRates?.length) ||
    (!composed?.lines?.length && rows.length > 0);

  const baseRowValue = composed?.lines?.length ? rows.find(row => row.isBase)?.value ?? null : baseValue;
  const visibleRows = rows.slice(0, MAX_PREVIEW_LINES);
  const hiddenCount = rows.length - visibleRows.length;
  const merchantName = merchant.name || '';
  // Only percent rates can headline — a $10 bounty times the share is a percent no one earns —
  // and exclusions can't: a varies program with one 0% carve-out is not "Up to 0%".
  const rowValues = rows
    .filter(row => !row.isExclusion && (row.unit ?? 'percent') === 'percent')
    .map(row => row.value)
    .filter(value => value !== null && value !== undefined);
  const maxValue = rowValues.length ? Math.max(...rowValues) : baseValue;
  const hasLowerRate = rows.some(row => row.value !== maxValue);
  // Without composed lines (no-API networks) the note carries the exclusions, so the flat number would overclaim.
  const showUpTo = !merchant.isClosedProgram && maxValue !== null && (hasLowerRate || (!composed?.lines?.length && !merchant.Brand_id));
  const variesLine = composed?.lines?.find(line => line.type === 'varies');
  const headline = maxValue !== null ? `${showUpTo ? 'Up to ' : ''}${maxValue}%` : variesLine?.text || formatMerchantRate(merchant) || '—';
  const boldValue = maxValue;

  return (
    <div className={cn('creator-rate-preview', { closed: merchant.isClosedProgram })}>
      {creatorsSeeBreakdown && (
        <div className='preview-headline'>
        <div className='preview-headline-rate'>
          {merchant.Brand_id && <PartnerBadge size={18} />}
          <span className='preview-rate-value'>{headline}</span>
        </div>
        {merchant.isClosedProgram ? (
          <span className='preview-status-chip'>Closed program</span>
        ) : detail.hasIncreasedRate ? (
          <span className='preview-status-chip increased'>
            <FontAwesomeIcon icon={faArrowCircleUp} /> Increased rate
          </span>
        ) : null}
        </div>
      )}
      {noteDraft !== null ? (
        <div className='preview-note editing'>
          <textarea
            autoFocus
            rows={4}
            maxLength={255}
            value={noteDraft}
            placeholder='Shown to creators alongside the rates above.'
            onChange={({ target }) => setNoteDraft(target.value)}
          />
          {disclaimerNotes.noteHasLinks(noteDraft) && (
            <div className='preview-note-live'>
              <span className='preview-note-label'>Creators will see:</span> {renderNoteWithLinks(noteDraft)}
            </div>
          )}
          <div className='preview-note-actions'>
            <button className='preview-note-save' disabled={savingNote} onClick={saveNote}>
              Save note
            </button>
            <button className='preview-note-cancel' disabled={savingNote} onClick={() => setNoteDraft(null)}>
              Cancel
            </button>
            <button
              className='preview-note-add-link'
              disabled={savingNote}
              onClick={() => setNoteDraft(`${noteDraft}${noteDraft && !noteDraft.endsWith(' ') ? ' ' : ''}[text to show](https://link-destination)`)}
            >
              + Hyperlink
            </button>
          </div>
        </div>
      ) : note ? (
        <div className='preview-note'>
          <span className='preview-note-label'>Note:</span> {renderNoteWithLinks(note)}
          {merchant.isSMSWinner ? (
            <button className='preview-note-edit' onClick={() => setNoteDraft(note)}>
              Edit
            </button>
          ) : (
            <span className='preview-note-empty'>Creators read the live listing's note — edit it there.</span>
          )}
        </div>
      ) : (
        merchant.isSMSWinner && (
          <button className='preview-note-add' onClick={() => setNoteDraft('')}>
            + Add note
          </button>
        )
      )}
      {!creatorsSeeBreakdown && (
        <div className='preview-rates'>
          <div className='preview-empty'>
            No creator preview — one flat rate and no note means creators just see the rate in the UI. Add a note above to show them a breakdown.
          </div>
        </div>
      )}
      {creatorsSeeBreakdown && visibleRows.length > 0 && (
        <>
          <div className='preview-rates-header'>All {merchantName} rates</div>
          <div className='preview-rates'>
            {visibleRows.map((row, index) => (
              <RateRow
                key={index}
                label={`${row.label}${row.until ? ` until ${row.until}` : ''}`}
                sublabel={row.sublabel}
                value={row.valueText}
                isActive={row.isBase ? row.value === boldValue : row.value === boldValue && baseRowValue !== boldValue}
                isIncrease={
                  // The up arrow marks a deliberate bump above a ShopMy-network brand's own base
                  // rate — external networks' higher registers are program structure, not a bump.
                  merchant.source === 'shopmyshelf' &&
                  !row.isBase &&
                  baseRowValue !== null &&
                  row.value !== null &&
                  row.value > baseRowValue
                }
              />
            ))}
            {hiddenCount > 0 && <div className='preview-more'>+ {hiddenCount} more rates</div>}
          </div>
        </>
      )}
      {creatorsSeeBreakdown && !visibleRows.length && baseValue === null && (
        <div className='preview-rates'>
          <div className='preview-empty'>No creator rate decoded for this merchant.</div>
        </div>
      )}
      {creatorsSeeBreakdown && (
        <div className='preview-footer'>
          <span>Updated {merchant.updatedAt ? moment(merchant.updatedAt).format('MMM D, YYYY') : '—'}</span>
          <span className='preview-guide'>Creator guide ↗</span>
        </div>
      )}
    </div>
  );
};

const RateRow = ({ label, sublabel, value, isActive, isIncrease }) => (
  <div className={cn('preview-rate-row', { active: isActive })}>
    <span className='preview-rate-label'>
      {label}
      {isIncrease && <FontAwesomeIcon className='preview-rate-increase' icon={faArrowCircleUp} />}
      {sublabel && <span className='preview-rate-sublabel'>{sublabel}</span>}
    </span>
    <span className='preview-rate-amount'>{value}</span>
  </div>
);

const TermRow = ({ label, detail, warn }) => (
  <div className={cn('term-row', { warn })}>
    <div className='term-label'>{label}</div>
    <div className='term-detail'>{detail}</div>
  </div>
);

const BackLink = () => (
  <Link className='merchant-detail-back' to='/merchantsV2'>
    <FontAwesomeIcon icon={faArrowLeft} /> All merchants
  </Link>
);

const describeMissingDisclaimer = merchant => {
  const sourceLabel = getSourceLabel(merchant.source);
  if (merchant.source === 'shopmyshelf') {
    return 'ShopMy-network brands set their rates directly — see ShopMy rates above. Commission disclaimers are composed for Impact, CJ, Rakuten, and Awin programs.';
  }
  if (!SNAPSHOT_SOURCES.includes(merchant.source)) {
    return `${sourceLabel} only reports a flat rate — commission disclaimers are composed for Impact, CJ, Rakuten, and Awin programs. The manual disclaimer above is what creators see.`;
  }
  if (!merchant.stable_merchant_id) return `This ${sourceLabel} row has no network program ID yet, so no commission disclaimer can be attached to it.`;
  return `No commission disclaimer has been composed for ${sourceLabel} program ${merchant.stable_merchant_id} yet. The nightly loader writes one once the program appears in a commissions snapshot.`;
};

const formatObservedAt = (value, format = 'MMM D, YYYY · h:mm A') => (value ? moment(value).format(format) : '—');
const formatPercent = (value, fallback = '—') => (value === null || value === undefined ? fallback : `${value}%`);

const CATALOG_GROUP_TYPE_LABELS = { shopify_collection: 'Shopify collection' };
const describeCatalogGroupType = type => (type ? CATALOG_GROUP_TYPE_LABELS[type] || type.replace(/_/g, ' ') : null);

const FACT_KIND_ORDER = { base: 0, conditional: 1, tier: 2, bonus: 3 };
const sortRateFacts = facts => _.sortBy(facts, [fact => FACT_KIND_ORDER[fact.kind] ?? 9, fact => -(parseFloat(fact.value) || 0), 'ruleKey']);

const formatFactRate = fact => {
  if (fact.value === null || fact.value === undefined) return 'n/a';
  if (fact.unit === 'percent') return `${fact.value}%`;
  return fact.currency && fact.currency !== 'USD' ? `${fact.value} ${fact.currency}` : `$${fact.value}`;
};

const rateTextForLine = line => {
  if (!line.rate) return line.rateText || line.text;
  const formatted = line.rate.value === 0 ? '0%' : formatFactRate(line.rate);
  return line.rateText?.startsWith('Up to') ? `Up to ${formatted}` : formatted;
};

const factSubjectLabel = fact =>
  fact.subject ||
  (fact.subjectValues?.length ? fact.subjectValues.join(', ') : null) ||
  GENERIC_SUBJECTS[fact.subjectType] ||
  (fact.kind === 'base' ? 'Everything' : 'Select items');

const describeFactSubject = fact => (fact.scope ? `${factSubjectLabel(fact)} · ${fact.scope}` : factSubjectLabel(fact));

const describeFactWindow = fact => {
  if (!fact.startsAt && !fact.endsAt) return null;
  const start = fact.startsAt ? moment(fact.startsAt).format('MMM D, YYYY') : 'open';
  const end = fact.endsAt ? moment(fact.endsAt).format('MMM D, YYYY') : 'open';
  return `${start} → ${end}`;
};

const describeFactRule = fact => {
  const parts = [];
  if (fact.networkDescription) parts.push(fact.networkDescription);
  (fact.conditions || []).forEach(condition => {
    if (condition.list) {
      parts.push(`${condition.field || 'items'} in list "${condition.list.name || condition.list.id}"${condition.list.itemCount ? ` (${condition.list.itemCount.toLocaleString()} items)` : ''}`);
    } else if (condition.field) {
      parts.push([condition.field, condition.operator, (condition.values || []).join(', ')].filter(Boolean).join(' '));
    }
  });
  if (fact.threshold) {
    const { min, max, metric } = fact.threshold;
    parts.push(`Threshold ${min ?? ''}${max ? `–${max}` : '+'}${metric ? ` ${metric}` : ''}`);
  }
  if (fact.basis) parts.push(`per ${fact.basis}`);
  return parts.join(' · ') || null;
};

export default MerchantDetail;
